import React, { FunctionComponent, useState } from "react";
import { useLingui } from "@lingui/react";
import { AJR, IJR } from "../settings";

export interface Props {
  company: string;
}

interface LanguageSensitiveSetting {
  nl: string;
  fr: string;
}
export interface WhiteLabelSettings {
  name: string;
  showDisclaimer: boolean;
  disclaimer: LanguageSensitiveSetting;
  bannerLink: LanguageSensitiveSetting;
  applicationName: LanguageSensitiveSetting;
  printDisclaimer: LanguageSensitiveSetting;
  pensionLink: LanguageSensitiveSetting;
  insuranceLink: LanguageSensitiveSetting;
}

const WhiteLabelContext = React.createContext<WhiteLabelSettings>({
  name: "",
  applicationName: { fr: "", nl: "" },
  bannerLink: { fr: "", nl: "" },
  disclaimer: { fr: "", nl: "" },
  showDisclaimer: true,
  printDisclaimer: { fr: "", nl: "" },
  pensionLink: { nl: "", fr: "" },
  insuranceLink: { nl: "", fr: "" },
});

const isLanguageSensitiveSetting = (setting: unknown): setting is LanguageSensitiveSetting =>
  typeof setting === "object";

export const useWhiteLabelSetting = (name: keyof WhiteLabelSettings) => {
  const {
    i18n: { locale },
  } = useLingui();
  const settings = React.useContext(WhiteLabelContext);
  const setting = settings[name];

  if (isLanguageSensitiveSetting(setting)) {
    return setting[locale as "nl" | "fr"].replace("{AJR}", AJR.toString()).replace("{IJR}", IJR.toString());
  }
  return setting;
};

export const WhiteLabelProvider: FunctionComponent<Props> = ({ children, company }) => {
  const [error, setError] = useState<Error>();
  const [loading, setLoading] = useState(true);
  const [whiteLabelSettings, setWhiteLabelSettings] = useState<WhiteLabelSettings>();
  React.useEffect(() => {
    const fn = async () => {
      try {
        setError(undefined);
        setLoading(true);
        const [settings] = await Promise.all([
          import(`../assets/${company}/settings.json`),
          import(`../assets/${company}/styles.css`),
        ]);
        setWhiteLabelSettings(settings);
      } catch (e) {
        setError(e as Error);
      } finally {
        setLoading(false);
      }
    };
    fn();
  }, [company]);

  if (error) {
    return <div>Unknown client</div>;
  }
  if (loading) {
    return <div></div>;
  }
  return (
    <WhiteLabelContext.Provider value={whiteLabelSettings as WhiteLabelSettings}>{children}</WhiteLabelContext.Provider>
  );
};

WhiteLabelProvider.displayName = "WhiteLabelProvider";
